import { useApp } from "../../services/app-context";
import AWS from 'aws-sdk';
import { useUser } from "../../services/user-context";
import { CHATBOT_BOT_ALIAS_ID, CHATBOT_BOT_ID, REGION } from "../../env";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as ChatIcon } from "../../assets/icons/chat.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg";
import { Button } from "../../components/Button";
import { Comment } from "react-loader-spinner";
import { ShowToast } from "../../services/toast";


const getSessionId = () => {
  // generate uuid
  const uuid = crypto.randomUUID();
  return uuid;
}
 


AWS.config.region = REGION;

const TextCard = ({ text, sender, isPreviousSameSender }: { text: string, sender: string, isPreviousSameSender: boolean }) => {
  return (
    <div className={`flex flex-col gap-2 ${sender === "user" ? "items-end" : "items-start"}`}>
      {!isPreviousSameSender && <div className={`text-[10px] text-black font-semibold`}>
        {sender === "user" ? "You" : "Support"}
      </div>}
      <div className={`bg-[#F0F0F0] max-w-[65%] py-2 px-3 rounded-lg ${sender === "user" ? "self-end" : "self-start"}`}>
        {text}
      </div>
    </div>
  )
}

const ImageCard = ({ image, onButtonClick, sender, isPreviousSameSender }: { image: any, onButtonClick?: (value: string) => void, sender: string, isPreviousSameSender: boolean }) => {
  return (
    <div className={`flex flex-col gap-2 ${sender === "user" ? "items-end" : "items-start"}`}>
      {!isPreviousSameSender && <div className={`text-[10px] text-black font-semibold`}>
        {sender === "user" ? "You" : "Support"}
      </div>}
      <div className={`bg-[#F0F0F0] max-w-[65%] flex flex-col gap-4 py-2 px-3 rounded-lg ${sender === "user" ? "self-end" : "self-start"}`}>
        {image?.title && <div className="text-lg font-semibold">{image?.title}</div>}
        <div className="flex flex-col gap-2">
          {image?.buttons?.map((button: any) => (
            <Button key={button.text} className="!py-1.5" variant="outline" onClick={() => onButtonClick && onButtonClick(button?.value)}>{button?.text}</Button>
          ))}
        </div>
      </div>
    </div>
  )
}

export function Chatbot() {
  const {isChatbotOpen, setIsChatbotOpen} = useApp();
  const { credentials } = useUser() as any;
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState<string>("");
  const lexRuntimeRef = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const sessionIdRef = useRef<string>("");

  const messageDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (credentials?.AccessKeyId && credentials?.SecretAccessKey && credentials?.SessionToken) {
      lexRuntimeRef.current = new AWS.LexRuntimeV2({
        region: REGION,
        credentials: {
          accessKeyId: credentials?.AccessKeyId,
          secretAccessKey: credentials?.SecretAccessKey,
          sessionToken: credentials?.SessionToken,
        },
      });
    }
  }, [credentials]);


  const sendMessage = (input: string) => {
    setMessages([...messages, { content: input, contentType: "PlainText", sender: "user" }]);
    setInput("");
    setLoading(true);
    if (!sessionIdRef.current) {
      sessionIdRef.current = getSessionId();
    }
    lexRuntimeRef.current.recognizeText({
      botId: CHATBOT_BOT_ID,
      botAliasId: CHATBOT_BOT_ALIAS_ID,
      sessionId: sessionIdRef.current,
      text: input,
      localeId: "en_US",
    }, (err, data) => {
      setLoading(false);
      if (err) {
        ShowToast({
          message: "Failed to send message",
          type: "error",
        });
      } else {
        setMessages(prev => [...prev, ...(data?.messages || []).map((msg: any) => ({ ...msg, sender: "bot" }))]);
      }
    });
  }

  const RenderMessage = ({ message, handleButtonClick, isPreviousSameSender }: { message: any, handleButtonClick?: (value: string) => void, isPreviousSameSender: boolean }) => {
    if (message.contentType === "PlainText") {
      return <TextCard text={message.content} sender={message.sender} isPreviousSameSender={isPreviousSameSender} />
    }
    if (message.contentType === "ImageResponseCard") {
      return <ImageCard image={message.imageResponseCard} onButtonClick={handleButtonClick} sender={message.sender} isPreviousSameSender={isPreviousSameSender} />
    }
    return <TextCard text={message.content} sender={message.sender} isPreviousSameSender={isPreviousSameSender} />
  }

  useEffect(() => {
    if (messageDivRef.current) {
      messageDivRef.current.scrollTop = messageDivRef.current.scrollHeight;
    }
  }, [messages, loading]);

  return (
    <>
      {!isChatbotOpen && <button onClick={() => setIsChatbotOpen(!isChatbotOpen)} className="fixed flex items-center justify-center bottom-2 right-2 h-[70px] w-[70px] hover:opacity-80 bg-PraxisDarkRed rounded-full" style={{ filter: "drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))" }}>
        <ChatIcon className="w-[41px] h-[41px]" />
      </button>}
      {isChatbotOpen && (
        <div className="w-[475px] h-[600px] flex flex-col mx-auto fixed bottom-2 right-2 bg-white rounded-b-[21px] shadow-lg">
          <div className="bg-PraxisDarkRed py-4 px-[18px] rounded-t-[21px] flex items-center justify-between">
            <span className="text-white font-medium text-[15px]">Support Chatbot</span>
            <button className="flex items-center justify-center" onClick={() => setIsChatbotOpen(false)}>
              <CloseIcon className="w-6 h-6 [&_path]:stroke-white" />
            </button>
          </div>
          <div className="p-5 rounded flex flex-col gap-2 flex-1 overflow-y-scroll bg-gray-50" ref={messageDivRef}>
            {messages.map((msg, i) => (
              <RenderMessage
                key={i}
                message={msg}
                handleButtonClick={(value) => {
                  sendMessage(value);
                }}
                isPreviousSameSender={i > 0 && messages[i - 1].sender === msg.sender}
              />
            ))}
            {loading &&
              <div>
                <Comment
                  visible={loading}
                  height="70"
                  width="80"
                  color="var(--PraxisDarkRed)"
                  backgroundColor="#F0F0F0"
                /></div>}
          </div>
          <div className="flex p-5">
            <input
              className="flex-grow h-[58px] p-2 rounded-[10px] placeholder:text-[#8E8E8E] bg-[#F6F6F6]"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage(input)}
              placeholder="Type your message..."
            />
          </div>
        </div>
      )}
    </>
  )
}